import { User } from "db/definitions";
import React, { useEffect, useState } from "react";
import { useTheme } from "../../hooks/useSingleton";
import { Button, ProgressDisplay, useProgress } from "@factorearth/component-library";
import styles from "./Profile.module.scss";
import { uploadOfflineDatabaseToS3, uploadImageBinariesToS3InBatches } from "db/utils/indexedDBManager";
import { recordSentry } from "utils/recordSentry";
import { useNavigate } from "react-router";
import { Typography } from "@factorearth/component-library";
import { fetchAuthSession } from "@aws-amplify/core";
import { LambdaClient, InvokeCommand } from "@aws-sdk/client-lambda";
import { Buffer } from "buffer";

interface Props {
	online: boolean;
	signOut: () => void;
	user: User | null | undefined;
	version: string;
}

function Profile(props: Props) {
	const { signOut, online, user, version } = props;
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const [userData, setUserData] = useState<User | null>(user || null);
	const { progress, setProgress } = useProgress({
		initial: {
			variant: "info",
			message: "",
			title: "",
			value: 0,
			canClose: true,
			show: false,
		},
	});

	useEffect(() => {
		if (user && user.roles) {
			setUserData(user);
		}
	}, [user]);

	const color = useTheme();

	// Which S3 upload is running. Both buttons are disabled while either runs, so a double tap cannot start a second
	// export (each one holds a read transaction over the whole database) or interleave two uploads.
	const [s3Busy, setS3Busy] = useState<"" | "db" | "images">("");

	async function uploadDatabase() {
		if (s3Busy) return;
		setS3Busy("db");
		try {
			await uploadOfflineDatabaseToS3(setProgress, { appVersion: version });
		} catch (error) {
			recordSentry({ error, msg: "Unhandled error in Profile DB upload" });
		} finally {
			setS3Busy("");
		}
	}
	const handleSignOut = () => {
		signOut();
		navigate("/");
	};

	const [exportTestResult, setExportTestResult] = useState<{ url?: string; error?: string; stats?: Record<string, unknown> } | null>(null);

	const handleTestExportImages = async (lambdaPayload: Record<string, unknown> = {}) => {
		setLoading(true);
		setExportTestResult(null);
		try {
			const { credentials } = await fetchAuthSession();
			const lambdaClient = new LambdaClient({ region: "us-west-1", credentials });
			const command = new InvokeCommand({
				FunctionName: `ExportImagesTest-${import.meta.env.VITE_APP_ENV !== "production" ? "staging" : "production"}`,
				Payload: Buffer.from(JSON.stringify(lambdaPayload)),
			});
			const response = await lambdaClient.send(command);
			const payload = JSON.parse(new TextDecoder().decode(response.Payload));
			if (payload.success) {
				setExportTestResult({ url: payload.presignedUrl, stats: payload.stats });
			} else {
				setExportTestResult({ error: payload.error ?? "Unknown error" });
			}
		} catch (err) {
			setExportTestResult({ error: err instanceof Error ? err.message : String(err) });
		} finally {
			setLoading(false);
		}
	};

	const handleUploadImageBinaries = async () => {
		if (s3Busy) return;
		setS3Busy("images");
		setProgress({
			value: 0,
			variant: "info",
			message: "Preparing image data",
			title: "Uploading Images",
			canClose: false,
			show: true,
		});
		try {
			await uploadImageBinariesToS3InBatches(setProgress);
		} catch (error) {
			recordSentry({ error, msg: "Unhandled error in Profile image upload" });
		} finally {
			setS3Busy("");
		}
	};

	return (
		<div className={styles.profileWrapper}>
			{progress.show && <ProgressDisplay colors={color} progress={progress} setProgress={setProgress} />}
			{user ? (
				<div className={styles.profileContainer}>
					<main style={{ border: `${color.border.primary} 1px solid`, background: color.background.tertiary }} className={styles.profileInfo}>
						<Typography variant={"Heading 3"} textColor={color.text.primary}>
							Hello!
						</Typography>
						<Typography
							variant={"Heading 2"}
							textColor={color.text.primary}
							style={{
								backgroundColor: color.background.primary,
								color: color.buttonBackground.filled,
								border: `3px solid ${color.buttonBackground.filled}`,
							}}
							className={styles.nameIcon}>
							{`${user?.username.slice(0, 2).toUpperCase()}`}
						</Typography>
						<Typography variant={"Paragraphy Body"} textColor={color.text.primary} className={styles.userInfoItem}>
							Username: <strong>{user?.username || "unknown"}</strong>
						</Typography>
						<Typography variant={"Paragraphy Body"} textColor={color.text.primary} className={styles.userInfoItem}>
							Email: <strong>{user?.email || "unknown"}</strong>
						</Typography>
						<Typography variant={"Paragraphy Body"} textColor={color.text.primary} className={styles.userInfoItem}>
							Role(s): <strong>{userData?.roles.map((role) => role.name).join(", ") || "unknown"}</strong>
						</Typography>
						<Typography variant={"Paragraphy Body"} textColor={color.text.primary} className={styles.userInfoItem}>
							Group: <strong>{user?.group || "unknown"}</strong>
						</Typography>
					</main>
					<main style={{ border: `${color.border.primary} 1px solid`, background: color.background.primary }} className={styles.profileInfo}>
						<Typography variant={"Paragraphy Body"} textColor={color.text.primary}>
							You are on{" "}
							<strong
								onClick={() => {
									navigate("/release-notes");
								}}
								className={styles.versionNum}>
								{version}
							</strong>{" "}
							of Record
						</Typography>
						<div className={styles.signOut}>
							{online ? <Button colorPalette={color} onClick={handleSignOut} disabled={loading} variant="filled" label="Sign Out" /> : null}
							<Button
								colorPalette={color}
								onClick={uploadDatabase}
								disabled={loading || s3Busy !== ""}
								variant="outlined"
								label={s3Busy === "db" ? "Uploading DB..." : "Upload DB to S3"}
								data-test="profile-upload-db"
							/>
							<Button
								colorPalette={color}
								onClick={handleUploadImageBinaries}
								disabled={loading || s3Busy !== ""}
								variant="outlined"
								label={s3Busy === "images" ? "Uploading Images..." : "Upload Images to S3"}
								data-test="profile-upload-images"
							/>
							{/* THIS IS FOR EXPERIMENTAL PURPOSES */}
							<Button
								colorPalette={color}
								onClick={() => handleTestExportImages()}
								disabled={loading}
								variant="outlined"
								label={loading ? "Running Export Test..." : "[DEV] Test Image Export (DynamoDB)"}
							/>
							<Button
								colorPalette={color}
								onClick={() => handleTestExportImages({ mode: "direct-s3", groups: ["FactorEarth", "Metcalf", "QualityAssurance"] })}
								disabled={loading}
								variant="outlined"
								label={loading ? "Running Export Test..." : "[DEV] Test Image Export (Direct S3)"}
							/>
							{exportTestResult && (
								<div
									style={{
										marginTop: 8,
										padding: 12,
										border: `1px solid ${color.border.primary}`,
										borderRadius: 6,
										background: color.background.tertiary,
									}}>
									{exportTestResult.error ? (
										<Typography variant="Paragraphy Body" textColor="red">
											Error: {exportTestResult.error}
										</Typography>
									) : (
										<>
											<Typography variant="Paragraphy Body" textColor={color.text.primary}>
												<strong>Scanned:</strong> {String(exportTestResult.stats?.scanned)} &nbsp;
												<strong>Streamed:</strong> {String(exportTestResult.stats?.streamed)} &nbsp;
												<strong>Skipped:</strong> {String(exportTestResult.stats?.skipped)} &nbsp;
												<strong>Duration:</strong> {String(exportTestResult.stats?.durationReadable)}
											</Typography>
											<a href={exportTestResult.url} target="_blank" rel="noreferrer" style={{ color: color.buttonBackground.filled }}>
												Download ZIP
											</a>
										</>
									)}
								</div>
							)}
						</div>
					</main>
				</div>
			) : null}
		</div>
	);
}

export default Profile;
